'use client'

import { useEffect, useState } from 'react'
import { usePathname } from '@/hooks/usePathname'
import { PowerSyncContext } from '@powersync/react'
import type { PowerSyncDatabase } from '@powersync/web'
import { getPowerSync, disconnectPowerSync } from '@/lib/powersync'
import { logPsLifecycle } from '@/lib/powersync/lifecycle-log'
import { useAuth } from '@/components/AuthProvider'
import { isMarketingPath } from '@/lib/route-gates'

// How long `user` may be null before we actually tear PowerSync down. Long
// enough to ride out transient auth flaps, short enough that a real sign-out
// releases the SQLite connection promptly.
const TEARDOWN_DELAY_MS = 3000

export function PowerSyncProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const pathname = usePathname()
  const skip = isMarketingPath(pathname)
  const [db, setDb] = useState<PowerSyncDatabase | null>(null)

  useEffect(() => {
    // On auth-entry / public pages don't touch the singleton: no connect
    // (fetchCredentials would run with a half-provisioned invited session
    // and stall the setup-password render), no disconnect (a logged-in user
    // passing through /agb shouldn't tear down their sync).
    if (skip) return
    if (!user) {
      if (!db) return
      // Debounce teardown. Auth state transiently flaps `user` to null on a
      // token refresh, a guard bounce, or a background getUser() revalidation,
      // then restores it within a second or two. Tearing down + recreating
      // PowerSync on each flap collides two PowerSyncDatabase instances on
      // imki.sqlite (the capacitor adapter keys native connections by filename)
      // → "database is locked (code 5)". Wait before tearing down; if `user`
      // returns, the effect cleanup cancels it and the existing instance is
      // reused untouched. A real sign-out leaves `user` null past the delay.
      logPsLifecycle('teardown-scheduled', `${TEARDOWN_DELAY_MS}ms`)
      let fired = false
      const t = setTimeout(() => {
        fired = true
        logPsLifecycle('teardown-fired')
        void disconnectPowerSync()
        setDb(null)
      }, TEARDOWN_DELAY_MS)
      return () => {
        clearTimeout(t)
        // Cleanup runs when user/skip changes (or on unmount). If the timer
        // hadn't fired, the flap resolved before the delay — instance kept.
        if (!fired) logPsLifecycle('teardown-cancelled')
      }
    }
    const live = getPowerSync()
    if (live) logPsLifecycle('provider-active')
    setDb(live)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, skip])

  // Auth-entry / public routes render without a Provider. Pages here never
  // call usePowerSync (guarded by isMarketingPath in route-gates.ts).
  if (skip) return <>{children}</>

  // Anonymous visitors (landing, login, legal pages) get the app shell
  // without a PowerSync connection. Hooks that call usePowerSync live on
  // authenticated routes only, so they never mount in this branch.
  if (!user) return <>{children}</>

  if (!db) return null
  return <PowerSyncContext.Provider value={db}>{children}</PowerSyncContext.Provider>
}
